<i18n src="@/lang/protocols.yaml"></i18n>

<template lang="pug">
.tab-pane.card-body
    h2.mb-4 {{ $t('tabs.schutzziel2.title') }}
    .row
        .col-md-4
            .card.border-success(v-if="validSchutzziel2")
                .card-body.text-center
                    font-awesome-icon.text-success.mb-4(icon="check-circle", size="4x")
                    p.mb-0(v-html="$t('tabs.schutzziel2.result.success')")
            .card.border-warning(v-else)
                .card-body.text-center
                    font-awesome-icon.text-warning.mb-4(icon="exclamation-circle", size="4x")
                    p.mb-0(v-html="$t('tabs.schutzziel2.result.warning', { percentage: ratio })")

            .card.bg-light.border-0.my-4.text-center: .card-body
                .card-title: h4 Gesamt

                h6.mt-4 Verbrennungsluftbedarf
                p
                    span.mr-3 q#[sub Bed]:
                    | {{ protocol.qBed.toFixed(1) }} m³

                h6.mt-4 Verbrennungsluftvolumenstrom
                p
                    span.mr-3 q#[sub VLanr] vorher:
                    | {{ protocol.qVLanrBefore.toFixed(1) }} m³
                p
                    span.mr-3 q#[sub VLanr] nachher:
                    | {{ qVLanrAfter.toFixed(1) }} m³

            button.btn.btn-block.btn-primary.mt-4(@click="saveAndContinue") Speichern &amp; Weiter

        .col.mt-4
            .mb-5(v-if="hasAld")
                h6.mb-4 Bei der Verwendung von Außenluft-Durchlässen (ALD):
                .form-group
                    label.label-title Vom Hersteller der ALD angegebener Luftvolumenstrom bei 4 Pa in m³/h:
                    numeric-input(v-model.number="protocol.data.qAld")
                    small.text-danger {{ qAldDefaultWarning }}

            .mb-4
                .h6.d-inline-block Wählen Sie zu treffende Maßnahmen pro Raum, um das Schutzziel 2 zu erreichen:
                help-text-modal.font-weight-normal(:body="$t('tabs.schutzziel2.helptext')")

            section(v-for="(room, idx) in protocol.data.rooms")
                strong {{ getRoomName(room.name) }}

                .row(v-if="room.isVlr")
                    .col-md-8.small(v-if="room.doorType !== 'OPEN'")
                        .form-group
                            label Maßnahme an der Tür (in Richtung Aufstellungsraum)
                            select.custom-select.custom-select-sm(
                                v-model="room.doorTypeNew",
                                :disabled="betterDoorTypes(room).length == 0"
                            )
                                option(:value="undefined") Keine Maßnahme
                                option(:value="doorType.value", v-for="doorType in betterDoorTypes(room)") {{ doorType.text }}
                    .col-md-8.pt-4(v-else)
                        p Permanent offener Durchgang. Keine Türmaßnahme nötig.

                    .col-md-4.small
                        .form-group
                            label Anzahl Außenluft-Durchlässe (ALD)
                            input.form-control.form-control-sm(type="number", min="0", v-model.number="room.numAld")

                    .col-md-12.small
                        .card.bg-light.border-0: .row
                            .col-lg-4.border-right
                                .d-flex.justify-content-between.p-2
                                    .text-nowrap q#[sub VLanr] vorher:
                                    .text-nowrap {{ room.qVLanr(protocol.n, false).toFixed(1) }} m²
                            .col-lg-4.border-right
                                .d-flex.justify-content-between.p-2
                                    .text-nowrap q#[sub VLanr] nachher:
                                    .text-nowrap {{ room.qVLanr(protocol.n, true, qAld).toFixed(1) }} m²
                            .col-lg-4.border-right(:class="{ 'text-success': qVLanrImprovement(room) > 0 }")
                                .d-flex.justify-content-between.p-2
                                    .text-nowrap Verbesserung:
                                    .text-nowrap +{{ qVLanrImprovement(room).toFixed(1) }} m³

                hr.my-4

            character-count-textarea(v-model="protocol.data.schutzziel2.description")
</template>

<script>
import { Protocol } from 'trgi'
import { doorTypes, roomNames } from '@/constants'
import CharacterCountTextarea from '@/components/CharacterCountTextarea'
import HelpTextModal from '@/components/HelpTextModal'
import NumericInput from '@/components/NumericInput'

import { library } from '@fortawesome/fontawesome-svg-core'
import { faCheckCircle, faExclamationCircle } from '@fortawesome/free-solid-svg-icons'

library.add(faCheckCircle, faExclamationCircle)

export default {
    components: {
        CharacterCountTextarea,
        HelpTextModal,
        NumericInput
    },
    props: {
        value: {
            type: Protocol,
            validator: function (value) {
                // TODO: define minimum requirements for Schutzziel2 Tab to show up
                return value.data.rooms.length >= 1
            },
            default: function () {
                return new Protocol()
            }
        }
    },
    data () {
        const protocol = new Protocol(this.value)
        return {
            protocol,
            doorTypes,
            roomNames
        }
    },
    computed: {
        validSchutzziel2 () {
            return this.ratio >= 100
        },
        ratio () {
            return Math.floor(100 / this.protocol.qBed * this.qVLanrAfter)
        },
        hasAld () {
            return this.protocol.data.rooms.find(room => room.numAld > 0)
        },
        qAld () {
            // use a default value of 3 for better UX - but show a warning
            return this.protocol.data.qAld || 3
        },
        qAldDefaultWarning () {
            return this.protocol.data.qAld ? '' : 'Bitte geben Sie den Wert der ALD aus den Herstellerangaben an. Standard-Wert ist 3.'
        },
        qVLanrAfter () {
            // can't use protocol.qVLanrAfter, because it may not have a qAld yet (see this.qAld())
            return this.protocol.data.rooms.reduce((acc, room) => acc + room.qVLanr(this.protocol.n, true, this.qAld), 0)
        }
    },
    watch: {
        // When Protocol gets modified on another Tab, refresh Local Protocol Variable
        value (value) {
            this.protocol = new Protocol(value)
        },
        // When Local Protocol achieves a Valid Schutzziel 2, let the Parent View know
        validSchutzziel2 (value) {
            this.$emit('update', value)
        }
    },
    created () {
        this.$emit('update', this.validSchutzziel2)
    },
    methods: {
        getRoomName (roomName) {
            // this should be put in an external lib
            if (roomName.length === 2) {
                return this.roomNames.find(e => e.value === roomName).text
            } else {
                // compatibility fallback for older protocols
                return roomName
            }
        },
        betterDoorTypes (room) {
            // FIXME: clarify by renaming: this function provides doortypes, that are "better" in terms of "would be an improvement"

            // null and OPEN values removed
            const doorTypes = this.doorTypes.filter(e => e.value && e.value !== 'OPEN')

            if (!room.doorType) { return doorTypes }

            let cur = parseInt(room.doorType.split('.')[0])
            return doorTypes.filter(e => parseInt(e.value && e.value.split('.')[0]) > cur)
        },
        qVLanrImprovement (room) {
            return room.qVLanr(this.protocol.n, true, this.qAld) - room.qVLanr(this.protocol.n, false)
        },
        saveAndContinue () {
            this.$emit('save', new Protocol(this.protocol))
        }
    }
}
</script>
